<template>
  <div
    class="comment"
    :class="[
      { 'comment--internal': value.isInternal, 'comment--self': isSelf },
      'comment--type-' + ((value.author && value.author.onModel) || 'User').toLowerCase(),
    ]"
  >
    <div class="comment__content">
      <div class="comment__message">
        <div class="comment__message-header">
          <div class="comment__message-header-left">
            <span class="comment__author-name" v-if="value.author">{{ value.author.name }}</span>
            <span class="comment__author-name" v-if="!value.author && name">{{ name }}</span>
            <span
              class="comment__date"
              v-tooltip="{ content: createdAtFormatted.LLL, delay: { show: 400, hide: 100 } }"
              >{{ createdAtFormatted.fromNow }}</span
            >
          </div>
          <DropdownComponent class="comment__action">
            <ButtonComponent :icon="{ icon: ['fal', 'ellipsis'], size: 'lg' }" />
            <template #dropdown="{ close }">
              <TaskCreateButton
                v-if="scope !== 'Task' && $auth.isAuthenticated"
                :reference="{ onModel: 'Comment', ref: value.id }"
                theme="default"
                @click.native="close"
              />
            </template>
          </DropdownComponent>
        </div>
        <div class="comment_message-text" v-html="SimpleMarkdownToHtml(value.message)"></div>
      </div>
      <ButtonComponent
        v-if="$auth.isAuthenticated && !value.isProcessed"
        :icon="['fal', 'check-circle']"
        class="comment__action"
        :promise="promise"
        @click="process"
        :label="$t('markAsProcessed')"
      />
      <slot name="actions" />
      <DropdownComponent
        v-if="value.attachments && value.attachments.length"
        class="comment__attachments"
        :close-on-click="true"
      >
        <ButtonComponent
          :icon="['fal', 'file']"
          :label="`${value.attachments.length} ${$tc('attachment', value.attachments.length).toLowerCase()}`"
          class="text--small"
        />
        <template #dropdown>
          <FileItem
            v-for="(item, index) in value.attachments"
            :key="'attachment_' + index"
            :value="item"
            @click.prevent="download(item)"
            readonly
          />
          <Alert>{{ $tc('externalFileDisclaimer') }}</Alert>
        </template>
      </DropdownComponent>
    </div>
    <div class="comment__meta">
      <DeliveryLog
        v-if="deliveryLogs.length"
        theme="pill"
        class="mt-1 self-start"
        :resource="$t('invite')"
        :value="deliveryLogs"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import CommentAPI from '@/api/Comment'
import { DownloadUrl, SimpleMarkdownToHtml, StripHTML } from '@/utils'

import { DropdownComponent } from 'vue-elder-dropdown'
import TaskButtonList from '@/components/Task/ButtonList'
import TaskCreateButton from '@/components/Task/CreateButton'
import { AlertComponent as Alert } from 'vue-elder-alert'
import FileItem from './FileItem.vue'
import DeliveryLog from '@/components/DeliveryLog'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  inject: ['scope'],
  data() {
    return {
      promise: null,
    }
  },
  computed: {
    deliveryLogs() {
      if (!this.value.deliveryLogs) return []
      return this.value.deliveryLogs
    },
    name() {
      if (!this.value.scope) return
      return this.value.scope
        .map(s => {
          switch (s.onModel) {
            case 'Project':
              return this.$t('theWholeProject')
            default:
              return s.name
          }
        })
        .join(' / ')
    },
    undoneTasks() {
      if (!this.value || !this.value.tasks) return []
      return this.value.tasks.filter(t => t.status !== 'done')
    },
    createdAtFormatted() {
      if (!this.value.createdAt) return {}
      return {
        LLL: moment(this.value.createdAt).format('LLL'),
        fromNow: moment(this.value.createdAt).fromNow(),
      }
    },
    isSelf() {
      if (!this.$path('value.author.id')) return
      return this.$path('value.author.id') === this.$path('$store.state.user.id')
    },
  },
  methods: {
    process() {
      this.promise = CommentAPI.mutate({ id: this.value.id, isProcessed: true }).then(data => {
        this.value.isProcessed = true
        this.value.processedBy = data.processedBy

        this.$emit('processed', this.value)
      })
    },
    download(file) {
      DownloadUrl(file.url, file.name)
    },
    SimpleMarkdownToHtml(val) {
      return SimpleMarkdownToHtml(StripHTML(val))
    },
  },
  components: {
    DropdownComponent,
    TaskButtonList,
    TaskCreateButton,
    FileItem,
    Alert,
    DeliveryLog,
  },
}
</script>

<style lang="scss">
.comment {
  &--type-contact {
    .comment__message {
      border-top-left-radius: 0px;
    }
  }

  &--type-user {
    .comment__content {
      flex-direction: row-reverse;
    }

    .comment__meta {
      text-align: end;
    }

    .comment__message {
      border-bottom-right-radius: 0px;
    }
  }

  &--self {
    .comment__message {
      background-color: #d6e4eb;
      color: black;
    }
  }

  &--internal {
    .comment__message {
      background-color: #fdecaf;
      color: black;
    }
  }

  &__content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    max-width: 100%;
  }

  &__message {
    position: relative;
    // background-color: $grey;
    background-color: #f6e3e1;
    padding: 1rem 1.2rem;
    border-radius: 10px;
    white-space: pre-line;
    max-width: 600px;

    &-internal {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      font-size: 0.8em;
      color: css-darken('warning', 5%);
      margin-top: 5px;
    }

    a {
      text-decoration: underline;
      color: inherit;
    }
  }

  &__message-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5rem;
    margin-bottom: 0.25rem;

    &-left {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
    }
  }

  &__author-name {
    font-size: 0.8em;
    color: black;
    font-weight: 600;
  }

  &__date {
    font-size: 0.8em;
    color: #555;
  }

  &__attachments {
    .kvass-file__thumbnail {
      cursor: pointer;
    }
    .kvass-file__thumbnail-download {
      display: none;
    }
    .elder-dropdown__content-box {
      gap: 0.25rem;
    }
    .elder-dropdown__content {
      min-width: 300px;
    }
  }

  &__action {
    font-size: 0.8em !important;

    .elder-dropdown__content {
      font-size: 1em;
    }

    .elder-dropdown__trigger {
      button {
        border: none;
      }

      .elder-button__icon {
        padding: 0.25rem 0.75rem;
      }
    }
  }

  &__meta {
    opacity: 0.5;
    font-size: 0.8em;
    padding-left: 0.5rem;
    margin-top: 0.5rem;

    & > *:not(:first-child) {
      position: relative;
      margin-left: 0.75rem;

      &::before {
        content: '\00B7';
        position: absolute;
        top: 0;
        left: -0.5rem;
      }
    }
  }
}
</style>
